import React, { useMemo } from "react";

import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    ListItemText,
    MenuItem,
    OutlinedInput,
    TextField,
} from "@mui/material";

import { LeadReportStatus } from "helpers/leads/generalReports";

import * as Styled from "../../../LeadGeneralReportsPage/LeadGeneralReportsPage.styled";
import {
    ALL_STATUSES,
    STATUS_COLORS,
} from "../../../LeadGeneralReportsPage/LeadGeneralReportsPage.types";
import {
    getLeadHistoryFilterError,
    LeadHistoryFilters,
} from "../LeadHistoryPage.helpers";

interface Props {
    filters: LeadHistoryFilters;
    onChange: (next: LeadHistoryFilters) => void;
    onApply: () => void;
    isLoading: boolean;
}

const DATE_PRESETS: {
    id: LeadHistoryFilters["datePreset"];
    label: string;
}[] = [
    { id: "last7", label: "Last 7 days" },
    { id: "last30", label: "Last 30 days" },
    { id: "last90", label: "Last 90 days" },
    { id: "custom", label: "Custom range" },
];

const checkboxSx = {
    p: 0.5,
    color: "#CBD5E1",
    "&.Mui-checked": {
        color: "#ED6C02",
    },
};

const MULTI_SELECT_MENU_PROPS = { variant: "menu" as const };

const renderSelectValue = (text: string) => (
    <Styled.SelectValueText title={text || undefined}>
        {text || "\u200B"}
    </Styled.SelectValueText>
);

const SelectActions: React.FC<{
    selectAllLabel: string;
    onSelectAll: () => void;
    onClear: () => void;
    selectAllDisabled?: boolean;
    clearDisabled?: boolean;
}> & { muiSkipListHighlight?: boolean } = ({
    selectAllLabel,
    onSelectAll,
    onClear,
    selectAllDisabled,
    clearDisabled,
}) => (
    <Styled.SelectActionsRow onKeyDown={(event) => event.stopPropagation()}>
        <Styled.SelectActionButton
            type="button"
            disabled={selectAllDisabled}
            onClick={onSelectAll}
        >
            {selectAllLabel}
        </Styled.SelectActionButton>
        <Styled.SelectActionButton
            type="button"
            disabled={clearDisabled}
            onClick={onClear}
        >
            Clear
        </Styled.SelectActionButton>
    </Styled.SelectActionsRow>
);

SelectActions.muiSkipListHighlight = true;

const HistoryFilterBar = ({ filters, onChange, onApply, isLoading }: Props) => {
    const rangeError = getLeadHistoryFilterError(filters);

    const statusRenderValue = useMemo(() => {
        if (!filters.statuses.length) return "";
        if (filters.statuses.length === ALL_STATUSES.length) {
            return "All Statuses";
        }
        return filters.statuses
            .map((status) => STATUS_COLORS[status].label)
            .join(", ");
    }, [filters.statuses]);

    return (
        <Styled.FilterCard elevation={0}>
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: {
                        xs: "1fr",
                        md: "minmax(180px, 1.2fr) minmax(180px, 1fr) 168px",
                    },
                    gap: "16px",
                    alignItems: "start",
                    width: "100%",
                }}
            >
                <Styled.FilterField $grow={1.2}>
                    <Styled.StyledFormControl size="small">
                        <Styled.StyledInputLabel
                            id="history-date-range-label"
                            shrink
                        >
                            Date Range
                        </Styled.StyledInputLabel>
                        <Styled.StyledSelect
                            labelId="history-date-range-label"
                            size="small"
                            notched
                            input={<OutlinedInput notched label="Date Range" />}
                            value={filters.datePreset}
                            onChange={(event) => {
                                const preset = event.target
                                    .value as LeadHistoryFilters["datePreset"];
                                onChange({
                                    ...filters,
                                    datePreset: preset,
                                    startDate:
                                        preset === "custom"
                                            ? filters.startDate
                                            : null,
                                    endDate:
                                        preset === "custom"
                                            ? filters.endDate
                                            : null,
                                });
                            }}
                        >
                            {DATE_PRESETS.map((preset) => (
                                <MenuItem key={preset.id} value={preset.id}>
                                    {preset.label}
                                </MenuItem>
                            ))}
                        </Styled.StyledSelect>
                    </Styled.StyledFormControl>
                    {filters.datePreset === "custom" && (
                        <Styled.DateInputRow direction="row" gap={1} mt={1}>
                            <TextField
                                type="date"
                                size="small"
                                fullWidth
                                value={filters.startDate ?? ""}
                                onChange={(event) =>
                                    onChange({
                                        ...filters,
                                        startDate: event.target.value,
                                    })
                                }
                            />
                            <TextField
                                type="date"
                                size="small"
                                fullWidth
                                value={filters.endDate ?? ""}
                                onChange={(event) =>
                                    onChange({
                                        ...filters,
                                        endDate: event.target.value,
                                    })
                                }
                            />
                        </Styled.DateInputRow>
                    )}
                </Styled.FilterField>

                <Styled.FilterField>
                    <Styled.StyledFormControl size="small">
                        <Styled.StyledInputLabel
                            id="history-status-label"
                            shrink
                        >
                            Status
                        </Styled.StyledInputLabel>
                        <Styled.StyledSelect
                            labelId="history-status-label"
                            size="small"
                            multiple
                            displayEmpty
                            notched
                            input={<OutlinedInput notched label="Status" />}
                            MenuProps={MULTI_SELECT_MENU_PROPS}
                            value={filters.statuses}
                            onChange={(event) => {
                                onChange({
                                    ...filters,
                                    statuses: event.target
                                        .value as LeadReportStatus[],
                                });
                            }}
                            renderValue={() =>
                                renderSelectValue(statusRenderValue)
                            }
                        >
                            <SelectActions
                                selectAllLabel="Select All Statuses"
                                selectAllDisabled={
                                    filters.statuses.length ===
                                    ALL_STATUSES.length
                                }
                                clearDisabled={!filters.statuses.length}
                                onSelectAll={() =>
                                    onChange({
                                        ...filters,
                                        statuses: [...ALL_STATUSES],
                                    })
                                }
                                onClear={() =>
                                    onChange({ ...filters, statuses: [] })
                                }
                            />
                            {ALL_STATUSES.map((status) => {
                                const meta = STATUS_COLORS[status];
                                return (
                                    <MenuItem key={status} value={status}>
                                        <Checkbox
                                            size="small"
                                            checked={filters.statuses.includes(
                                                status,
                                            )}
                                            sx={checkboxSx}
                                        />
                                        <ListItemText primary={meta.label} />
                                    </MenuItem>
                                );
                            })}
                        </Styled.StyledSelect>
                    </Styled.StyledFormControl>
                </Styled.FilterField>

                <Styled.FilterField $grow={0}>
                    {isLoading && (
                        <div className="circle-loader">
                            <CircularProgress size={20} />
                        </div>
                    )}
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={onApply}
                        style={{ minWidth: "unset" }}
                    >
                        Apply Filters
                    </Button>
                </Styled.FilterField>
            </Box>

            {rangeError && (
                <Box
                    sx={{
                        color: "#DC2626",
                        fontSize: 12,
                        fontWeight: 500,
                        mt: 1,
                    }}
                >
                    {rangeError}
                </Box>
            )}
        </Styled.FilterCard>
    );
};

export default HistoryFilterBar;
